import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {classMap} from 'lit/directives/class-map.js';
import styles from './style.scss';
import {when} from 'lit/directives/when.js';

export const SWITCH_ELEMENT_TAG = 'fly-switch';

export type SwitchChangeEventDetail = {
  /** Whether the switch is "on" or "off." */
  on: boolean;
};

export enum SwitchIntent {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

/** Custom change event for `<fly-switch>`. */
export type SwitchChangeEvent = CustomEvent<SwitchChangeEventDetail>;

/**
 * UI component that displays a toggle switch.
 */
@customElement(SWITCH_ELEMENT_TAG)
export default class Switch extends FlywheelComponent {
  /** Property representing whether the switch is toggled "on." */
  @property({type: Boolean})
  on = false;

  /** Determines whether or not the switch can be toggled and shows it as disabled. */
  @property({type: Boolean})
  disabled = false;

  /** Determines whether or not the switch can be toggled but does not look like disabled. */
  @property({type: Boolean})
  readonly = false;

  @property({type: String})
  label = '';

  @property({type: String})
  intent = SwitchIntent.PRIMARY;

  static get styles() {
    return [styles];
  }

  private dispatchChangeEvent() {
    const event = new CustomEvent<SwitchChangeEventDetail>('change', {
      detail: {on: this.on},
    });

    this.dispatchEvent(event);
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('on')) this.dispatchChangeEvent();
  }

  /** Returns true if the switch is on. */
  isOn() {
    return this.on;
  }

  /** Returns true if the switch is off. */
  isOff() {
    return !this.on;
  }

  render() {
    return html`
      <div class="fly-form__field">
        ${when(
          this.label,
          () => html`
            <span class="fly-form__field__label"
              ><slot name="label">${this.label}</slot></span
            >
          `
        )}
        <label
          class="fly-switch ${classMap({
            'fly-switch--disabled': this.disabled,
            'fly-switch--readonly': this.readonly,
          })}"
        >
          <input
            ?checked=${this.on}
            type="checkbox"
            @click=${(e: Event) => {
              if (this.disabled || this.readonly) {
                e.preventDefault();
                return;
              }

              this.on = !this.on;
            }}
          />
          <span
            class=${classMap({
              slider: this.intent === SwitchIntent.PRIMARY,
              'slider--checked': this.on,
              'slider slider--secondary':
                this.intent === SwitchIntent.SECONDARY,
            })}
          ></span>
          <slot></slot>
        </label>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [SWITCH_ELEMENT_TAG]: Switch;
  }
}
